"use client";

import { useState, useEffect, useRef } from "react";
import { Note } from "@/types/note";
import {
  getMetadata,
  saveNote,
  generateId,
  getAutoSuggestion,
} from "@/lib/storage";
import { useOCAccount } from "@/hooks/useOCAccount";
import { OCAnalytics } from "@opencampus/ochub-utils";

interface ComboBoxProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  placeholder: string;
}

function ComboBox({
  label,
  value,
  onChange,
  suggestions,
  placeholder,
}: ComboBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const filtered = suggestions.filter((s) =>
        s.toLowerCase().includes(value.toLowerCase()),
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions(suggestions);
    }
  }, [value, suggestions]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex-1 min-w-0" ref={wrapperRef}>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent bg-white"
        />
        {isOpen && filteredSuggestions.length > 0 && (
          <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => {
                  onChange(suggestion);
                  setIsOpen(false);
                }}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
              >
                {suggestion}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface NoteEditorProps {
  existingNote?: Note | null;
  onClose: () => void;
  onSave: () => void;
}

export default function NoteEditor({
  existingNote,
  onClose,
  onSave,
}: NoteEditorProps) {
  const [category, setCategory] = useState(existingNote?.category || "");
  const [occasion, setOccasion] = useState(existingNote?.occasion || "");
  const [topic, setTopic] = useState(existingNote?.topic || "");
  const [content, setContent] = useState(existingNote?.content || "");
  const [metadata, setMetadata] = useState({
    categories: [] as string[],
    occasions: [] as string[],
    topics: [] as string[],
  });
  const [isSaving, setIsSaving] = useState(false);

  // Get OCAccount for tracking
  const { ocId } = useOCAccount();

  useEffect(() => {
    setMetadata(getMetadata());

    // Auto-suggest category, occasion, and topic for new notes
    if (!existingNote) {
      const suggestion = getAutoSuggestion();
      if (suggestion) {
        if (suggestion.category) setCategory(suggestion.category);
        if (suggestion.occasion) setOccasion(suggestion.occasion);
        if (suggestion.topic) setTopic(suggestion.topic);
      }
    }
  }, [existingNote]);

  const handleSave = () => {
    setIsSaving(true);

    const now = new Date().toISOString();
    const note: Note = {
      id: existingNote?.id || generateId(),
      category,
      occasion,
      topic,
      content,
      createdAt: existingNote?.createdAt || now,
      updatedAt: now,
    };

    saveNote(note);

    // Track user_save_notes event
    try {
      const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;
      OCAnalytics.getInstance().trackEvent("user_save_notes", {
        note_category: category,
        note_class: occasion,
        note_topic: topic,
        note_length: content.length,
        word_count: wordCount,
        ocid: ocId,
      });
    } catch (e) {
      // Analytics not initialized yet, ignore
    }

    setTimeout(() => {
      setIsSaving(false);
      onSave();
    }, 300);
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header with combo boxes */}
      <div className="bg-gradient-to-r from-brand-blue to-brand-cyan p-4 shadow-md">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <h1 className="text-white font-semibold text-lg">
            {existingNote ? "Edit Note" : "New Note"}
          </h1>
          <div className="w-10" />
        </div>

        <div className="flex gap-2">
          <ComboBox
            label="Category"
            value={category}
            onChange={setCategory}
            suggestions={metadata.categories}
            placeholder="e.g. Math"
          />
          <ComboBox
            label="Occasion"
            value={occasion}
            onChange={setOccasion}
            suggestions={metadata.occasions}
            placeholder="e.g. Lecture"
          />
          <ComboBox
            label="Topic"
            value={topic}
            onChange={setTopic}
            suggestions={metadata.topics}
            placeholder="e.g. Algebra"
          />
        </div>
      </div>

      {/* Notepad area */}
      <div className="flex-1 overflow-hidden p-4 bg-gray-100">
        <div className="h-full rounded-lg shadow-lg overflow-hidden notepad-container">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Start typing your notes..."
            className="notepad-textarea"
          />
        </div>
      </div>

      {/* Save button */}
      <div className="p-4 bg-white border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full py-4 bg-gradient-to-r from-brand-blue to-brand-cyan text-white font-semibold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Saving...
            </span>
          ) : (
            "💾 Save Note"
          )}
        </button>
      </div>
    </div>
  );
}
